import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { ProductFormCard, ProductFormData } from '@/components/seller/products/ProductFormCard';
import { sellerService } from '@/services/seller';
import { imageService } from '@/services/image';
import { useToast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';

export function ProductRegisterPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();

  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductFormData[]>([
    {
      name: '',
      description: '',
      category: '',
      originalPrice: 0,
      stock: 20,
      imageFile: null,
      imageUrl: undefined
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const initializePage = async () => {
      try {
        setLoading(true);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          showToast(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }
        
        setRestaurantId(restaurantResult.restaurantId);
        
      } catch (error) {
        console.error('Initialize page error:', error);
        showToast('페이지 초기화 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [showToast]);

  const handleProductChange = (index: number, product: ProductFormData) => {
    setProducts(prev => prev.map((p, i) => i === index ? product : p));
  };

  const handleAddProduct = () => {
    setProducts(prev => [...prev, {
      name: '',
      description: '',
      category: '',
      originalPrice: 0,
      stock: 20,
      imageFile: null,
      imageUrl: undefined
    }]);
  };

  const handleRemoveProduct = (index: number) => {
    if (products.length <= 1) {
      showToast('최소 1개의 상품은 필요합니다');
      return;
    }

    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  const validateProducts = (): boolean => {
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      
      if (!product.name.trim()) {
        showToast(`${i + 1}번째 상품의 이름을 입력해주세요`);
        return false;
      }
      
      if (product.originalPrice <= 0) {
        showToast(`${i + 1}번째 상품의 가격을 입력해주세요`);
        return false;
      }
      
      if (product.stock <= 0) {
        showToast(`${i + 1}번째 상품의 재고를 입력해주세요`);
        return false;
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!restaurantId || !validateProducts()) return;
    
    try {
      setSaving(true);
      
      // 1. 먼저 상품 정보를 DB에 저장하여 productId 획득
      const productsToCreate = products.map(product => ({
        name: product.name.trim(),
        description: product.description?.trim() || undefined,
        category: product.category?.trim() || undefined,
        originalPrice: product.originalPrice,
        stock: product.stock,
        imageUrl: undefined // 나중에 업데이트할 예정
      }));

      const result = await sellerService.createProducts(restaurantId, productsToCreate);
      
      if (!result.success || !result.productIds) {
        throw new Error(result.error || '상품 등록에 실패했습니다');
      }

      // 2. 각 상품에 이미지 업로드 (생성된 productIds를 정확히 매칭)
      const imageUploadPromises = products.map(async (product, index) => {
        if (product.imageFile && result.productIds?.[index]) {
          const productId = result.productIds[index]; // 정확한 순서 매칭
          const uploadResult = await imageService.uploadProductImage(
            product.imageFile,
            productId
          );

          if (!uploadResult.success || !uploadResult.url) {
            throw new Error(uploadResult.error || '이미지 업로드에 실패했습니다');
          }

          const { error: updateError } = await supabase
            .from('products')
            .update({ image_url: uploadResult.url })
            .eq('id', productId);

          if (updateError) {
            throw new Error(updateError.message || '상품 이미지 URL 업데이트에 실패했습니다');
          }
        }
      });

      await Promise.all(imageUploadPromises);
      
      showToast('상품이 성공적으로 등록되었습니다');
      // 3초 후 내 가게로 이동
      setTimeout(() => {
        navigate('/seller/store');
      }, 3000);
      
    } catch (error) {
      console.error('Save products error:', error);
      showToast(error instanceof Error ? error.message : '상품 등록 중 오류가 발생했습니다');
    } finally {
      setSaving(false);
    }
  };

  const canSave = products.every(p => 
    p.name.trim() && 
    p.originalPrice > 0 && 
    p.stock > 0
  ) && !saving;

  if (loading) {
    return (
      <SellerPageContainer activeNavId="store">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">페이지를 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="store">
      <div className="pb-4">
        {/* 헤더 */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/seller/store')}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">상품 등록</h1>
        </div>

        {/* 안내 메시지 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">상품 등록 안내</p>
              <ul className="space-y-1 text-sm">
                <li>• 원하는 만큼 상품을 한 번에 등록할 수 있습니다</li>
                <li>• 이미지(JPG, PNG, WebP) 업로드 지원 (최대 20MB, 업로드 시 자동 리사이징)</li>
                <li>• 등록된 상품은 마감할인, 예약할인 설정에서 선택할 수 있습니다</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 상품 목록 */}
        <div className="space-y-4 mb-6">
          {products.map((product, index) => (
            <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-800">
                  상품 {index + 1}
                </h3>
                {products.length > 1 && (
                  <button
                    onClick={() => handleRemoveProduct(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    삭제
                  </button>
                )}
              </div>
              
              <ProductFormCard
                product={product}
                onChange={(updatedProduct) => handleProductChange(index, updatedProduct)}
                showCheckbox={false}
                showDeleteButton={false}
              />
            </div>
          ))}
        </div>

        {/* 상품 추가 버튼 */}
        <button
          onClick={handleAddProduct}
          className="w-full py-3 mb-6 border-2 border-dashed rounded-lg transition-colors border-green-300 text-green-600 hover:border-green-500 hover:bg-green-50"
        >
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            상품 추가 ({products.length}개)
          </div>
        </button>

        {/* 저장 버튼 */}
        <button
          onClick={handleSave}
          disabled={!canSave}
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            canSave
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? '등록 중...' : '상품 등록'}
        </button>

        {!canSave && !saving && (
          <p className="text-center text-sm text-gray-500 mt-2">
            모든 상품 정보를 입력해주세요
          </p>
        )}
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}
